//  ______                        _               _  _  _
//  |  _  \                      | |             | |(_)| |
//  | | | |  ___    _ __    ___  | |_    ___   __| | _ | |_
//  | | | | / _ \  | '_ \  / _ \ | __|  / _ \ / _` || || __|
//  | |/ / | (_) | | | | || (_) || |_  |  __/| (_| || || |_
//  |___/   \___/  |_| |_| \___/  \__|  \___| \__,_||_| \__|
//
// Generated by skir-typescript-gen
// Home: https://github.com/gepheum/skir-typescript-gen
//
// To install the Skir client library:
//   npm i skir-client

/// <reference types="./websocket_event.d.ts" />

import * as $ from "skir-client";
import * as x_activity from "./activity.js";
import * as x_call_utils from "./call_utils.js";
import * as x_chat_session from "./chat_session.js";
import * as x_connector from "./connector.js";
import * as x_meeting_common from "./meeting_common.js";
import * as x_meeting_stage from "./meeting_stage.js";
import * as x_meeting_state from "./meeting_state.js";
import * as x_methodology from "./methodology.js";
import * as x_sales_insights from "./sales_insights.js";

// -----------------------------------------------------------------------------
// enum BackToFrontEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. */
export class BackToFrontEvent extends $._EnumBase {}

function createValueOfBackToFrontEvent(initializer) {
  const { kind, value } = initializer;
  switch (kind) {
    case "meeting_stage": {
      return MeetingStageEvent.create(value);
    }
    case "transcript": {
      return TranscriptEvent.create(value);
    }
    case "insight": {
      return InsightEvent.create(value);
    }
    case "answer": {
      return AnswerEvent.create(value);
    }
    case "methodology_status": {
      return MethodologyStatusEvent.create(value);
    }
    case "realtime_insights": {
      return RealtimeInsightsEvent.create(value);
    }
    case "coaching_cue": {
      return CoachingCueEvent.create(value);
    }
    case "realtime_trigger": {
      return RealtimeTriggerEvent.create(value);
    }
    case "participant_join": {
      return ParticipantJoinEvent.create(value);
    }
    case "meeting_info_update": {
      return MeetingInfoUpdateEvent.create(value);
    }
    case "switch_demo_account": {
      return SwitchDemoAccountEvent.create(value);
    }
    case "chat_session_upserted": {
      return ChatSessionUpsertedEvent.create(value);
    }
    case "chat_session_deleted": {
      return ChatSessionDeletedEvent.create(value);
    }
    case "chat_messages_appended": {
      return ChatMessagesAppendedEvent.create(value);
    }
    case "sales_insights_run": {
      return SalesInsightsRunUpdatedEvent.create(value);
    }
    case "activity": {
      return ActivityPushEvent.create(value);
    }
  }
}

// -----------------------------------------------------------------------------
// struct MeetingStageEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `MeetingStageEvent.Mutable`. */
export class MeetingStageEvent extends $._FrozenBase {}

function initMeetingStageEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.userId = initializer.userId ?? "";
  target.accountId = initializer.accountId ?? BigInt(0);
  target.newStage = x_meeting_stage.MeetingStage.create(initializer.newStage ?? x_meeting_stage.MeetingStage.DEFAULT);
  target.joinedAt = initializer.joinedAt ?? $.Timestamp.UNIX_EPOCH;
  target.meetingUrl = initializer.meetingUrl ?? "";
  target.platform = x_meeting_state.MeetingSource.Platform.create(initializer.platform ?? x_meeting_state.MeetingSource.Platform.UNKNOWN);
  target.meetingName = initializer.meetingName ?? "";
  target.calendarEventId = initializer.calendarEventId ?? "";
  target.isInternal = initializer.isInternal ?? false;
}

// -----------------------------------------------------------------------------
// struct TranscriptEvent.Piece
// -----------------------------------------------------------------------------

/**
 * Deeply immutable. If you need mutability, use `TranscriptEvent.Piece.Mutable`.
 *
 * @alias TranscriptEvent.Piece
 */
class TranscriptEvent_Piece extends $._FrozenBase {}

function initTranscriptEvent_Piece(target, initializer) {
  target.text = initializer.text ?? "";
  target.startTimestamp = x_call_utils.Timestamp.create(initializer.startTimestamp ?? x_call_utils.Timestamp.DEFAULT);
  target.endTimestamp = x_call_utils.Timestamp.create(initializer.endTimestamp ?? x_call_utils.Timestamp.DEFAULT);
}

// -----------------------------------------------------------------------------
// struct TranscriptEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `TranscriptEvent.Mutable`. */
export class TranscriptEvent extends $._FrozenBase {}

function initTranscriptEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.participant = x_call_utils.Participant.create(initializer.participant ?? x_call_utils.Participant.DEFAULT);
  target.pieces = $._toFrozenArray(
    initializer.pieces || [],
    TranscriptEvent_Piece.create,
  );
  target.recordingId = initializer.recordingId ?? 0;
  target.isPartial = initializer.isPartial ?? false;
}

// -----------------------------------------------------------------------------
// enum InsightEvent.Type
// -----------------------------------------------------------------------------

/**
 * Deeply immutable.
 *
 * @alias InsightEvent.Type
 */
class InsightEvent_Type extends $._EnumBase {}

// -----------------------------------------------------------------------------
// struct InsightEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `InsightEvent.Mutable`. */
export class InsightEvent extends $._FrozenBase {}

function initInsightEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.eventId = initializer.eventId ?? BigInt(0);
  target.originalContent = initializer.originalContent ?? "";
  target.rephrasedContent = initializer.rephrasedContent ?? "";
  target.type = InsightEvent_Type.create(initializer.type ?? InsightEvent_Type.UNKNOWN);
  target.callsForAnswer = initializer.callsForAnswer ?? false;
  target.participant = x_call_utils.Participant.create(initializer.participant ?? x_call_utils.Participant.DEFAULT);
  target.secondsFromStart = initializer.secondsFromStart ?? 0;
  target.answer = initializer.answer ? AnswerEvent.create(initializer.answer) : null;
}

// -----------------------------------------------------------------------------
// enum AnswerEvent.Verdict
// -----------------------------------------------------------------------------

/**
 * Deeply immutable.
 *
 * @alias AnswerEvent.Verdict
 */
class AnswerEvent_Verdict extends $._EnumBase {}

// -----------------------------------------------------------------------------
// struct AnswerEvent.Source
// -----------------------------------------------------------------------------

/**
 * Deeply immutable. If you need mutability, use `AnswerEvent.Source.Mutable`.
 *
 * @alias AnswerEvent.Source
 */
class AnswerEvent_Source extends $._FrozenBase {}

function initAnswerEvent_Source(target, initializer) {
  target.heading = initializer.heading ?? "";
  target.excerpt = initializer.excerpt ?? "";
  target.documentId = initializer.documentId ?? "";
  target.originalFilename = initializer.originalFilename ?? "";
  target.contentType = initializer.contentType ?? "";
  target.fileSize = initializer.fileSize ?? null;
  target.sourceUrl = initializer.sourceUrl ?? "";
  target.connectorType = initializer.connectorType ? x_connector.Connector.Source.create(initializer.connectorType) : null;
}

// -----------------------------------------------------------------------------
// struct AnswerEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `AnswerEvent.Mutable`. */
export class AnswerEvent extends $._FrozenBase {}

function initAnswerEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.questionEventId = initializer.questionEventId ?? BigInt(0);
  target.answer = initializer.answer ?? "";
  target.confidence = initializer.confidence ?? 0;
  target.verdict = AnswerEvent_Verdict.create(initializer.verdict ?? AnswerEvent_Verdict.UNKNOWN);
  target.sources = $._toFrozenArray(
    initializer.sources || [],
    AnswerEvent_Source.create,
  );
  target.fromDocumentation = initializer.fromDocumentation ?? false;
}

// -----------------------------------------------------------------------------
// struct MethodologyStatusEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `MethodologyStatusEvent.Mutable`. */
export class MethodologyStatusEvent extends $._FrozenBase {}

function initMethodologyStatusEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.status = x_methodology.MethodologyStatus.create(initializer.status ?? x_methodology.MethodologyStatus.DEFAULT);
}

// -----------------------------------------------------------------------------
// struct ParticipantJoinEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `ParticipantJoinEvent.Mutable`. */
export class ParticipantJoinEvent extends $._FrozenBase {}

function initParticipantJoinEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.participant = x_call_utils.Participant.create(initializer.participant ?? x_call_utils.Participant.DEFAULT);
}

// -----------------------------------------------------------------------------
// struct ChatSessionUpsertedEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `ChatSessionUpsertedEvent.Mutable`. */
export class ChatSessionUpsertedEvent extends $._FrozenBase {}

function initChatSessionUpsertedEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.session = x_chat_session.ChatSession.create(initializer.session ?? x_chat_session.ChatSession.DEFAULT);
  target.originSessionId = initializer.originSessionId ?? "";
}

// -----------------------------------------------------------------------------
// struct ChatSessionDeletedEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `ChatSessionDeletedEvent.Mutable`. */
export class ChatSessionDeletedEvent extends $._FrozenBase {}

function initChatSessionDeletedEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.sessionId = initializer.sessionId ?? "";
  target.originSessionId = initializer.originSessionId ?? "";
}

// -----------------------------------------------------------------------------
// struct ChatMessagesAppendedEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `ChatMessagesAppendedEvent.Mutable`. */
export class ChatMessagesAppendedEvent extends $._FrozenBase {}

function initChatMessagesAppendedEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.sessionId = initializer.sessionId ?? "";
  target.messages = $._toFrozenArray(
    initializer.messages || [],
    x_chat_session.ChatMessage.create,
  );
  target.session = x_chat_session.ChatSession.create(initializer.session ?? x_chat_session.ChatSession.DEFAULT);
  target.originSessionId = initializer.originSessionId ?? "";
}

// -----------------------------------------------------------------------------
// enum CoachingCueEvent.CueType
// -----------------------------------------------------------------------------

/**
 * Deeply immutable.
 *
 * @alias CoachingCueEvent.CueType
 */
class CoachingCueEvent_CueType extends $._EnumBase {}

// -----------------------------------------------------------------------------
// struct CoachingCueEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `CoachingCueEvent.Mutable`. */
export class CoachingCueEvent extends $._FrozenBase {}

function initCoachingCueEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.cueType = CoachingCueEvent_CueType.create(initializer.cueType ?? CoachingCueEvent_CueType.UNKNOWN);
  target.priority = initializer.priority ?? 0;
  target.message = initializer.message ?? "";
  target.suggestedAction = initializer.suggestedAction ?? "";
  target.targetParticipantId = initializer.targetParticipantId ?? "";
  target.triggerReason = initializer.triggerReason ?? "";
}

// -----------------------------------------------------------------------------
// struct RealtimeTriggerEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `RealtimeTriggerEvent.Mutable`. */
export class RealtimeTriggerEvent extends $._FrozenBase {}

function initRealtimeTriggerEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.triggerType = initializer.triggerType ?? "";
  target.severity = initializer.severity ?? "";
  target.participantId = initializer.participantId ?? "";
  target.participantName = initializer.participantName ?? "";
  target.description = initializer.description ?? "";
}

// -----------------------------------------------------------------------------
// struct ParticipantInsights
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `ParticipantInsights.Mutable`. */
export class ParticipantInsights extends $._FrozenBase {}

function initParticipantInsights(target, initializer) {
  target.participantId = initializer.participantId ?? "";
  target.participantName = initializer.participantName ?? "";
  target.isInternal = initializer.isInternal ?? false;
  target.talkTimeSharePercent = initializer.talkTimeSharePercent ?? 0;
  target.speakingPaceWpm = initializer.speakingPaceWpm ?? 0;
  target.utteranceCount = initializer.utteranceCount ?? 0;
}

// -----------------------------------------------------------------------------
// struct RealtimeInsightsEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `RealtimeInsightsEvent.Mutable`. */
export class RealtimeInsightsEvent extends $._FrozenBase {}

function initRealtimeInsightsEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.participantInsights = $._toFrozenArray(
    initializer.participantInsights || [],
    ParticipantInsights.create,
  );
  target.meetingDurationSeconds = initializer.meetingDurationSeconds ?? 0;
  target.dominanceBalance = initializer.dominanceBalance ?? 0;
  target.questionRatio = initializer.questionRatio ?? 0;
  target.flowHealthScore = initializer.flowHealthScore ?? 0;
}

// -----------------------------------------------------------------------------
// struct MeetingInfoUpdateEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `MeetingInfoUpdateEvent.Mutable`. */
export class MeetingInfoUpdateEvent extends $._FrozenBase {}

function initMeetingInfoUpdateEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.meetingInfo = x_meeting_common.MeetingInfo.create(initializer.meetingInfo ?? x_meeting_common.MeetingInfo.DEFAULT);
  target.thumbnailUrl = initializer.thumbnailUrl ?? "";
  target.statusChanged = initializer.statusChanged ?? false;
  target.thumbnailChanged = initializer.thumbnailChanged ?? false;
}

// -----------------------------------------------------------------------------
// struct SwitchDemoAccountEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `SwitchDemoAccountEvent.Mutable`. */
export class SwitchDemoAccountEvent extends $._FrozenBase {}

function initSwitchDemoAccountEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.accountId = initializer.accountId ?? BigInt(0);
  target.userId = initializer.userId ?? "";
  target.externalContext = initializer.externalContext ?? "";
}

// -----------------------------------------------------------------------------
// struct SalesInsightsRunUpdatedEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `SalesInsightsRunUpdatedEvent.Mutable`. */
export class SalesInsightsRunUpdatedEvent extends $._FrozenBase {}

function initSalesInsightsRunUpdatedEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.runId = initializer.runId ?? "";
  target.summary = x_sales_insights.SalesInsightsRunSummary.create(initializer.summary ?? x_sales_insights.SalesInsightsRunSummary.DEFAULT);
  target.newEvents = $._toFrozenArray(
    initializer.newEvents || [],
    x_sales_insights.SalesInsightsProgressEvent.create,
  );
}

// -----------------------------------------------------------------------------
// struct ActivityPushEvent
// -----------------------------------------------------------------------------

/** Deeply immutable. If you need mutability, use `ActivityPushEvent.Mutable`. */
export class ActivityPushEvent extends $._FrozenBase {}

function initActivityPushEvent(target, initializer) {
  target.meetingId = initializer.meetingId ?? "";
  target.timestamp = initializer.timestamp ?? $.Timestamp.UNIX_EPOCH;
  target.item = x_activity.ActivityItem.create(initializer.item ?? x_activity.ActivityItem.DEFAULT);
  target.unreadCount = initializer.unreadCount ?? 0;
}

// -----------------------------------------------------------------------------
// Initialize the serializers
// -----------------------------------------------------------------------------

$._initModuleClasses(
  "websocket_event.skir",
  [
    {
      kind: "enum",
      ctor: BackToFrontEvent,
      createValueFn: createValueOfBackToFrontEvent,
      name: "BackToFrontEvent",
      variants: [
        {
          name: "UNKNOWN",
          number: 0,
        },
        {
          name: "meeting_stage",
          number: 1,
          type: {
            kind: "record",
            ctor: MeetingStageEvent,
          },
        },
        {
          name: "transcript",
          number: 2,
          type: {
            kind: "record",
            ctor: TranscriptEvent,
          },
        },
        {
          name: "insight",
          number: 3,
          type: {
            kind: "record",
            ctor: InsightEvent,
          },
        },
        {
          name: "answer",
          number: 4,
          type: {
            kind: "record",
            ctor: AnswerEvent,
          },
        },
        {
          name: "methodology_status",
          number: 8,
          type: {
            kind: "record",
            ctor: MethodologyStatusEvent,
          },
        },
        {
          name: "realtime_insights",
          number: 11,
          type: {
            kind: "record",
            ctor: RealtimeInsightsEvent,
          },
        },
        {
          name: "coaching_cue",
          number: 12,
          type: {
            kind: "record",
            ctor: CoachingCueEvent,
          },
        },
        {
          name: "realtime_trigger",
          number: 13,
          type: {
            kind: "record",
            ctor: RealtimeTriggerEvent,
          },
        },
        {
          name: "participant_join",
          number: 14,
          type: {
            kind: "record",
            ctor: ParticipantJoinEvent,
          },
        },
        {
          name: "meeting_info_update",
          number: 15,
          type: {
            kind: "record",
            ctor: MeetingInfoUpdateEvent,
          },
        },
        {
          name: "switch_demo_account",
          number: 16,
          type: {
            kind: "record",
            ctor: SwitchDemoAccountEvent,
          },
        },
        {
          name: "chat_session_upserted",
          number: 17,
          type: {
            kind: "record",
            ctor: ChatSessionUpsertedEvent,
          },
        },
        {
          name: "chat_session_deleted",
          number: 18,
          type: {
            kind: "record",
            ctor: ChatSessionDeletedEvent,
          },
        },
        {
          name: "chat_messages_appended",
          number: 19,
          type: {
            kind: "record",
            ctor: ChatMessagesAppendedEvent,
          },
        },
        {
          name: "sales_insights_run",
          number: 20,
          type: {
            kind: "record",
            ctor: SalesInsightsRunUpdatedEvent,
          },
        },
        {
          name: "activity",
          number: 21,
          type: {
            kind: "record",
            ctor: ActivityPushEvent,
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: MeetingStageEvent,
      initFn: initMeetingStageEvent,
      name: "MeetingStageEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "user_id",
          property: "userId",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "account_id",
          property: "accountId",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "int64",
          },
        },
        {
          name: "new_stage",
          property: "newStage",
          number: 4,
          type: {
            kind: "record",
            ctor: x_meeting_stage.MeetingStage,
          },
          mutableGetter: "mutableNewStage",
        },
        {
          name: "joined_at",
          property: "joinedAt",
          number: 5,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "meeting_url",
          property: "meetingUrl",
          number: 6,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "platform",
          property: "platform",
          number: 7,
          type: {
            kind: "record",
            ctor: x_meeting_state.MeetingSource.Platform,
          },
        },
        {
          name: "meeting_name",
          property: "meetingName",
          number: 8,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "calendar_event_id",
          property: "calendarEventId",
          number: 9,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "is_internal",
          property: "isInternal",
          number: 10,
          type: {
            kind: "primitive",
            primitive: "bool",
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: TranscriptEvent_Piece,
      initFn: initTranscriptEvent_Piece,
      name: "Piece",
      parentCtor: TranscriptEvent,
      fields: [
        {
          name: "text",
          property: "text",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "start_timestamp",
          property: "startTimestamp",
          number: 3,
          type: {
            kind: "record",
            ctor: x_call_utils.Timestamp,
          },
          mutableGetter: "mutableStartTimestamp",
        },
        {
          name: "end_timestamp",
          property: "endTimestamp",
          number: 4,
          type: {
            kind: "record",
            ctor: x_call_utils.Timestamp,
          },
          mutableGetter: "mutableEndTimestamp",
        },
      ],
      removedNumbers: [
        0,
        2,
      ],
    },
    {
      kind: "struct",
      ctor: TranscriptEvent,
      initFn: initTranscriptEvent,
      name: "TranscriptEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "participant",
          property: "participant",
          number: 2,
          type: {
            kind: "record",
            ctor: x_call_utils.Participant,
          },
          mutableGetter: "mutableParticipant",
        },
        {
          name: "pieces",
          property: "pieces",
          number: 3,
          type: {
            kind: "array",
            item: {
              kind: "record",
              ctor: TranscriptEvent_Piece,
            },
          },
          mutableGetter: "mutablePieces",
        },
        {
          name: "recording_id",
          property: "recordingId",
          number: 4,
          type: {
            kind: "primitive",
            primitive: "int32",
          },
        },
        {
          name: "is_partial",
          property: "isPartial",
          number: 5,
          type: {
            kind: "primitive",
            primitive: "bool",
          },
        },
      ],
    },
    {
      kind: "enum",
      ctor: InsightEvent_Type,
      name: "Type",
      parentCtor: InsightEvent,
      variants: [
        {
          name: "UNKNOWN",
          number: 0,
        },
        {
          name: "QUESTION",
          number: 1,
        },
        {
          name: "CLAIM",
          number: 2,
        },
        {
          name: "SUGGESTION",
          number: 3,
        },
        {
          name: "OBJECTION",
          number: 4,
        },
        {
          name: "WARNING",
          number: 5,
        },
      ],
    },
    {
      kind: "struct",
      ctor: InsightEvent,
      initFn: initInsightEvent,
      name: "InsightEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "event_id",
          property: "eventId",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "int64",
          },
        },
        {
          name: "original_content",
          property: "originalContent",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "rephrased_content",
          property: "rephrasedContent",
          number: 4,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "type",
          property: "type",
          number: 5,
          type: {
            kind: "record",
            ctor: InsightEvent_Type,
          },
        },
        {
          name: "calls_for_answer",
          property: "callsForAnswer",
          number: 6,
          type: {
            kind: "primitive",
            primitive: "bool",
          },
        },
        {
          name: "participant",
          property: "participant",
          number: 7,
          type: {
            kind: "record",
            ctor: x_call_utils.Participant,
          },
          mutableGetter: "mutableParticipant",
        },
        {
          name: "seconds_from_start",
          property: "secondsFromStart",
          number: 8,
          type: {
            kind: "primitive",
            primitive: "float32",
          },
        },
        {
          name: "answer",
          property: "answer",
          number: 9,
          type: {
            kind: "optional",
            other: {
              kind: "record",
              ctor: AnswerEvent,
            },
          },
        },
      ],
    },
    {
      kind: "enum",
      ctor: AnswerEvent_Verdict,
      name: "Verdict",
      parentCtor: AnswerEvent,
      variants: [
        {
          name: "UNKNOWN",
          number: 0,
        },
        {
          name: "TRUE",
          number: 1,
        },
        {
          name: "FALSE",
          number: 2,
        },
      ],
    },
    {
      kind: "struct",
      ctor: AnswerEvent_Source,
      initFn: initAnswerEvent_Source,
      name: "Source",
      parentCtor: AnswerEvent,
      fields: [
        {
          name: "heading",
          property: "heading",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "excerpt",
          property: "excerpt",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "document_id",
          property: "documentId",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "original_filename",
          property: "originalFilename",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "content_type",
          property: "contentType",
          number: 4,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "file_size",
          property: "fileSize",
          number: 5,
          type: {
            kind: "optional",
            other: {
              kind: "primitive",
              primitive: "int64",
            },
          },
        },
        {
          name: "source_url",
          property: "sourceUrl",
          number: 6,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "connector_type",
          property: "connectorType",
          number: 7,
          type: {
            kind: "optional",
            other: {
              kind: "record",
              ctor: x_connector.Connector.Source,
            },
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: AnswerEvent,
      initFn: initAnswerEvent,
      name: "AnswerEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "question_event_id",
          property: "questionEventId",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "int64",
          },
        },
        {
          name: "answer",
          property: "answer",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "confidence",
          property: "confidence",
          number: 4,
          type: {
            kind: "primitive",
            primitive: "float32",
          },
        },
        {
          name: "verdict",
          property: "verdict",
          number: 5,
          type: {
            kind: "record",
            ctor: AnswerEvent_Verdict,
          },
        },
        {
          name: "sources",
          property: "sources",
          number: 6,
          type: {
            kind: "array",
            item: {
              kind: "record",
              ctor: AnswerEvent_Source,
            },
          },
          mutableGetter: "mutableSources",
        },
        {
          name: "from_documentation",
          property: "fromDocumentation",
          number: 7,
          type: {
            kind: "primitive",
            primitive: "bool",
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: MethodologyStatusEvent,
      initFn: initMethodologyStatusEvent,
      name: "MethodologyStatusEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "status",
          property: "status",
          number: 2,
          type: {
            kind: "record",
            ctor: x_methodology.MethodologyStatus,
          },
          mutableGetter: "mutableStatus",
        },
      ],
    },
    {
      kind: "struct",
      ctor: ParticipantJoinEvent,
      initFn: initParticipantJoinEvent,
      name: "ParticipantJoinEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "participant",
          property: "participant",
          number: 2,
          type: {
            kind: "record",
            ctor: x_call_utils.Participant,
          },
          mutableGetter: "mutableParticipant",
        },
      ],
    },
    {
      kind: "struct",
      ctor: ChatSessionUpsertedEvent,
      initFn: initChatSessionUpsertedEvent,
      name: "ChatSessionUpsertedEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "session",
          property: "session",
          number: 2,
          type: {
            kind: "record",
            ctor: x_chat_session.ChatSession,
          },
          mutableGetter: "mutableSession",
        },
        {
          name: "origin_session_id",
          property: "originSessionId",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: ChatSessionDeletedEvent,
      initFn: initChatSessionDeletedEvent,
      name: "ChatSessionDeletedEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "session_id",
          property: "sessionId",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "origin_session_id",
          property: "originSessionId",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: ChatMessagesAppendedEvent,
      initFn: initChatMessagesAppendedEvent,
      name: "ChatMessagesAppendedEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "session_id",
          property: "sessionId",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "messages",
          property: "messages",
          number: 3,
          type: {
            kind: "array",
            item: {
              kind: "record",
              ctor: x_chat_session.ChatMessage,
            },
          },
          mutableGetter: "mutableMessages",
        },
        {
          name: "session",
          property: "session",
          number: 4,
          type: {
            kind: "record",
            ctor: x_chat_session.ChatSession,
          },
          mutableGetter: "mutableSession",
        },
        {
          name: "origin_session_id",
          property: "originSessionId",
          number: 5,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
      ],
    },
    {
      kind: "enum",
      ctor: CoachingCueEvent_CueType,
      name: "CueType",
      parentCtor: CoachingCueEvent,
      variants: [
        {
          name: "UNKNOWN",
          number: 0,
        },
        {
          name: "TALK_LESS",
          number: 1,
        },
        {
          name: "TALK_MORE",
          number: 2,
        },
        {
          name: "SLOW_DOWN",
          number: 3,
        },
        {
          name: "SPEED_UP",
          number: 4,
        },
        {
          name: "ASK_OPEN_QUESTION",
          number: 5,
        },
        {
          name: "PROBE_DEEPER",
          number: 6,
        },
        {
          name: "ACKNOWLEDGE_CONCERN",
          number: 7,
        },
        {
          name: "HANDLE_OBJECTION",
          number: 8,
        },
        {
          name: "CAPITALIZE_ON_INTEREST",
          number: 9,
        },
        {
          name: "RE_ENGAGE_PARTICIPANT",
          number: 10,
        },
        {
          name: "SUMMARIZE_UNDERSTANDING",
          number: 11,
        },
        {
          name: "PAUSE_FOR_EFFECT",
          number: 12,
        },
        {
          name: "ADDRESS_CONFUSION",
          number: 13,
        },
      ],
    },
    {
      kind: "struct",
      ctor: CoachingCueEvent,
      initFn: initCoachingCueEvent,
      name: "CoachingCueEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "cue_type",
          property: "cueType",
          number: 2,
          type: {
            kind: "record",
            ctor: CoachingCueEvent_CueType,
          },
        },
        {
          name: "priority",
          property: "priority",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "int32",
          },
        },
        {
          name: "message",
          property: "message",
          number: 4,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "suggested_action",
          property: "suggestedAction",
          number: 5,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "target_participant_id",
          property: "targetParticipantId",
          number: 6,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "trigger_reason",
          property: "triggerReason",
          number: 7,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: RealtimeTriggerEvent,
      initFn: initRealtimeTriggerEvent,
      name: "RealtimeTriggerEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "trigger_type",
          property: "triggerType",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "severity",
          property: "severity",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "participant_id",
          property: "participantId",
          number: 4,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "participant_name",
          property: "participantName",
          number: 5,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "description",
          property: "description",
          number: 6,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: ParticipantInsights,
      initFn: initParticipantInsights,
      name: "ParticipantInsights",
      fields: [
        {
          name: "participant_id",
          property: "participantId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "participant_name",
          property: "participantName",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "is_internal",
          property: "isInternal",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "bool",
          },
        },
        {
          name: "talk_time_share_percent",
          property: "talkTimeSharePercent",
          number: 6,
          type: {
            kind: "primitive",
            primitive: "float32",
          },
        },
        {
          name: "speaking_pace_wpm",
          property: "speakingPaceWpm",
          number: 7,
          type: {
            kind: "primitive",
            primitive: "float32",
          },
        },
        {
          name: "utterance_count",
          property: "utteranceCount",
          number: 8,
          type: {
            kind: "primitive",
            primitive: "int32",
          },
        },
      ],
      removedNumbers: [
        3,
        4,
        5,
        9,
      ],
    },
    {
      kind: "struct",
      ctor: RealtimeInsightsEvent,
      initFn: initRealtimeInsightsEvent,
      name: "RealtimeInsightsEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "participant_insights",
          property: "participantInsights",
          number: 2,
          type: {
            kind: "array",
            item: {
              kind: "record",
              ctor: ParticipantInsights,
            },
          },
          mutableGetter: "mutableParticipantInsights",
        },
        {
          name: "meeting_duration_seconds",
          property: "meetingDurationSeconds",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "float32",
          },
        },
        {
          name: "dominance_balance",
          property: "dominanceBalance",
          number: 4,
          type: {
            kind: "primitive",
            primitive: "float32",
          },
        },
        {
          name: "question_ratio",
          property: "questionRatio",
          number: 5,
          type: {
            kind: "primitive",
            primitive: "float32",
          },
        },
        {
          name: "flow_health_score",
          property: "flowHealthScore",
          number: 6,
          type: {
            kind: "primitive",
            primitive: "float32",
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: MeetingInfoUpdateEvent,
      initFn: initMeetingInfoUpdateEvent,
      name: "MeetingInfoUpdateEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "meeting_info",
          property: "meetingInfo",
          number: 2,
          type: {
            kind: "record",
            ctor: x_meeting_common.MeetingInfo,
          },
          mutableGetter: "mutableMeetingInfo",
        },
        {
          name: "thumbnail_url",
          property: "thumbnailUrl",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "status_changed",
          property: "statusChanged",
          number: 4,
          type: {
            kind: "primitive",
            primitive: "bool",
          },
        },
        {
          name: "thumbnail_changed",
          property: "thumbnailChanged",
          number: 5,
          type: {
            kind: "primitive",
            primitive: "bool",
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: SwitchDemoAccountEvent,
      initFn: initSwitchDemoAccountEvent,
      name: "SwitchDemoAccountEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "account_id",
          property: "accountId",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "int64",
          },
        },
        {
          name: "user_id",
          property: "userId",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "external_context",
          property: "externalContext",
          number: 4,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
      ],
    },
    {
      kind: "struct",
      ctor: SalesInsightsRunUpdatedEvent,
      initFn: initSalesInsightsRunUpdatedEvent,
      name: "SalesInsightsRunUpdatedEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "run_id",
          property: "runId",
          number: 2,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "summary",
          property: "summary",
          number: 3,
          type: {
            kind: "record",
            ctor: x_sales_insights.SalesInsightsRunSummary,
          },
          mutableGetter: "mutableSummary",
        },
        {
          name: "new_events",
          property: "newEvents",
          number: 4,
          type: {
            kind: "array",
            item: {
              kind: "record",
              ctor: x_sales_insights.SalesInsightsProgressEvent,
            },
          },
          mutableGetter: "mutableNewEvents",
        },
      ],
    },
    {
      kind: "struct",
      ctor: ActivityPushEvent,
      initFn: initActivityPushEvent,
      name: "ActivityPushEvent",
      fields: [
        {
          name: "meeting_id",
          property: "meetingId",
          number: 0,
          type: {
            kind: "primitive",
            primitive: "string",
          },
        },
        {
          name: "timestamp",
          property: "timestamp",
          number: 1,
          type: {
            kind: "primitive",
            primitive: "timestamp",
          },
        },
        {
          name: "item",
          property: "item",
          number: 2,
          type: {
            kind: "record",
            ctor: x_activity.ActivityItem,
          },
          mutableGetter: "mutableItem",
        },
        {
          name: "unread_count",
          property: "unreadCount",
          number: 3,
          type: {
            kind: "primitive",
            primitive: "int32",
          },
        },
      ],
    },
  ]
);
